import type { ConditionalRule, Condition, FormField } from '../types/form';

export interface FieldState {
  visible: boolean;
  enabled: boolean;
  required: boolean;
}

export interface PageNavigation {
  skipToPageId: string;
  triggered: boolean;
}

function evaluateCondition(
  condition: Condition,
  values: Record<string, unknown>,
  fieldIdToName: Record<string, string>
): boolean {
  // Look up value by field name (which is what react-hook-form uses)
  const fieldName = fieldIdToName[condition.fieldId];
  const fieldValue = fieldName ? values[fieldName] : values[condition.fieldId];
  const targetValue = condition.value;

  switch (condition.operator) {
    case 'equals':
      return fieldValue === targetValue;

    case 'not_equals':
      return fieldValue !== targetValue;

    case 'contains':
      if (typeof fieldValue === 'string' && typeof targetValue === 'string') {
        return fieldValue.toLowerCase().includes(targetValue.toLowerCase());
      }
      if (Array.isArray(fieldValue)) {
        return fieldValue.includes(targetValue);
      }
      return false;

    case 'not_contains':
      if (typeof fieldValue === 'string' && typeof targetValue === 'string') {
        return !fieldValue.toLowerCase().includes(targetValue.toLowerCase());
      }
      if (Array.isArray(fieldValue)) {
        return !fieldValue.includes(targetValue);
      }
      return true;

    case 'greater_than':
      if (typeof fieldValue === 'number' && typeof targetValue === 'number') {
        return fieldValue > targetValue;
      }
      return false;

    case 'less_than':
      if (typeof fieldValue === 'number' && typeof targetValue === 'number') {
        return fieldValue < targetValue;
      }
      return false;

    case 'is_empty':
      return (
        fieldValue === null ||
        fieldValue === undefined ||
        fieldValue === '' ||
        (Array.isArray(fieldValue) && fieldValue.length === 0)
      );

    case 'is_not_empty':
      return !(
        fieldValue === null ||
        fieldValue === undefined ||
        fieldValue === '' ||
        (Array.isArray(fieldValue) && fieldValue.length === 0)
      );

    default:
      return false;
  }
}

function evaluateRule(
  rule: ConditionalRule,
  values: Record<string, unknown>,
  fieldIdToName: Record<string, string>
): boolean {
  if (rule.conditions.length === 0) return true;

  if (rule.operator === 'and') {
    return rule.conditions.every((condition) =>
      evaluateCondition(condition, values, fieldIdToName)
    );
  } else {
    return rule.conditions.some((condition) =>
      evaluateCondition(condition, values, fieldIdToName)
    );
  }
}

export interface ConditionalLogicResult {
  fieldStates: Record<string, FieldState>;
  pageNavigations: PageNavigation[];
}

export function evaluateConditionalLogic(
  rules: ConditionalRule[],
  values: Record<string, unknown>,
  baseRequired: Record<string, boolean> = {},
  fields: FormField[] = []
): Record<string, FieldState> {
  const result = evaluateConditionalLogicFull(rules, values, baseRequired, fields);
  return result.fieldStates;
}

export function evaluateConditionalLogicFull(
  rules: ConditionalRule[],
  values: Record<string, unknown>,
  baseRequired: Record<string, boolean> = {},
  fields: FormField[] = []
): ConditionalLogicResult {
  const fieldStates: Record<string, FieldState> = {};
  const pageNavigations: PageNavigation[] = [];

  // Build a map from field ID to field name for value lookups
  const fieldIdToName: Record<string, string> = {};
  for (const field of fields) {
    fieldIdToName[field.id] = field.name;
  }

  // Initialize all fields as visible and enabled
  // Required state comes from the field definition
  for (const rule of rules) {
    if (rule.action !== 'skip_to_page' && !fieldStates[rule.targetFieldId]) {
      fieldStates[rule.targetFieldId] = {
        visible: true,
        enabled: true,
        required: baseRequired[rule.targetFieldId] || false,
      };
    }
  }

  // Apply rules
  for (const rule of rules) {
    const conditionMet = evaluateRule(rule, values, fieldIdToName);

    switch (rule.action) {
      case 'show':
        fieldStates[rule.targetFieldId].visible = conditionMet;
        break;

      case 'hide':
        fieldStates[rule.targetFieldId].visible = !conditionMet;
        break;

      case 'enable':
        fieldStates[rule.targetFieldId].enabled = conditionMet;
        break;

      case 'disable':
        fieldStates[rule.targetFieldId].enabled = !conditionMet;
        break;

      case 'require':
        fieldStates[rule.targetFieldId].required = conditionMet;
        break;

      case 'skip_to_page':
        if (rule.skipToPageId) {
          pageNavigations.push({
            skipToPageId: rule.skipToPageId,
            triggered: conditionMet,
          });
        }
        break;
    }
  }

  return { fieldStates, pageNavigations };
}

export function getDefaultFieldState(required: boolean = false): FieldState {
  return {
    visible: true,
    enabled: true,
    required,
  };
}
